import axios from 'axios';
import { getAuthHeaders, clearAuthData, getEmail, SESSION_EXPIRED_EVENT } from '../utils/auth';
import { getCache, setCache, invalidateCache, TTL } from '../utils/cache';
import env from '../config/environments';

const HR_API_URL = env.hrApiUrl;
export const CORE_API_URL = env.coreApiUrl;

/**
 * HR-portal backend (applicant/Django) instance.
 * Sends session cookie AND token header so both auth strategies work
 * during the migration (enforce_permission tries cookie first, then token).
 */
const baseApi = axios.create({
    baseURL: HR_API_URL,
    withCredentials: true,
});

/**
 * Core-API instance — handles authentication endpoints
 * (login, logout, /me, firebase-token).
 */
export const coreApi = axios.create({
    baseURL: CORE_API_URL,
    withCredentials: true,
});

/**
 * 401 response interceptor for the HR backend.
 * Clears local user data and redirects to login on session expiry.
 * Handles both legacy token errors and new session-based errors.
 */
baseApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const data = error.response.data;
            const errorMessage = typeof data === 'string' ? data : data?.error;
            console.warn(`Authentication failed: ${errorMessage}`);
            clearAuthData();
            window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
            return Promise.reject(new Error(errorMessage || 'Session expired. Please log in again.'));
        }
        return Promise.reject(error);
    }
);

/**
 * Makes an authenticated GET request
 * @param {string} url - The API endpoint
 * @param {object} config - Optional axios config
 * @returns {Promise} - Axios response promise
 */
export const authGet = async (url, config = {}) => {
    const response = await baseApi.get(url, {
        ...config,
        headers: {
            ...getAuthHeaders(),
            ...config.headers
        }
    });
    return response;
};

/**
 * Makes an authenticated POST request
 * @param {string} url - The API endpoint
 * @param {object} data - The request body
 * @param {object} config - Optional axios config
 * @returns {Promise} - Axios response promise
 */
export const authPost = async (url, data = {}, config = {}) => {
    const response = await baseApi.post(url, data, {
        ...config,
        headers: {
            ...getAuthHeaders(),
            ...config.headers
        }
    });
    return response;
};

/**
 * Makes an authenticated PUT request
 * @param {string} url - The API endpoint
 * @param {object} data - The request body
 * @param {object} config - Optional axios config
 * @returns {Promise} - Axios response promise
 */
export const authPut = async (url, data = {}, config = {}) => {
    const response = await baseApi.put(url, data, {
        ...config,
        headers: {
            ...getAuthHeaders(),
            ...config.headers
        }
    });
    return response;
};

/**
 * Makes an authenticated PATCH request
 * @param {string} url - The API endpoint
 * @param {object} data - The request body
 * @param {object} config - Optional axios config
 * @returns {Promise} - Axios response promise
 */
export const authPatch = async (url, data = {}, config = {}) => {
    const response = await baseApi.patch(url, data, {
        ...config,
        headers: {
            ...getAuthHeaders(),
            ...config.headers
        }
    });
    return response;
};

/**
 * Makes an authenticated DELETE request
 * @param {string} url - The API endpoint
 * @param {object} config - Optional axios config
 * @returns {Promise} - Axios response promise
 */
export const authDelete = async (url, config = {}) => {
    const response = await baseApi.delete(url, {
        ...config,
        headers: {
            ...getAuthHeaders(),
            ...config.headers
        }
    });
    return response;
};

/**
 * Authenticated API object with all HTTP methods
 * Usage: authApi.get('/endpoint'), authApi.post('/endpoint', data), etc.
 */
export const authApi = {
    get: authGet,
    post: authPost,
    put: authPut,
    patch: authPatch,
    delete: authDelete
};

/**
 * Resume screening engine endpoints.
 * Appli 1.0 = legacy comprehensive-prompt screener (`screen-resumes`).
 * Appli 2.0 = detector-based engine (`analysis-v2/resume/regenerate-detector`).
 * The engine selector on the resume screening page maps 1:1 to these.
 */
export const resumeScreeningApi = {
    // Appli 1.0 (legacy) - unchanged behaviour
    screenResumes: (jobPositionId, data = {}) =>
        authPost(`/job-position/${jobPositionId}/resume-screening/screen-resumes/`, data),
    // Appli 2.0 (detector engine)
    regenerateDetector: (jobPositionId, data = {}) =>
        authPost(`/job-position/${jobPositionId}/analysis-v2/resume/regenerate-detector/`, data),
};

/**
 * Interview screening eval3 (Appli 3.0) endpoints: trigger, twin read routes,
 * per-candidate pipeline status, and HR-defined interview flags CRUD.
 */
export const interviewScreeningApi = {
    // Appli 3.0 (eval3 engine) trigger - 202 {status: "started", analysis_run_id}
    generateEval3: (jobPositionId, data = {}) =>
        authPost(`/job-position/${jobPositionId}/analytics/pre-screening-interview-eval3/`, data),
    getEval3Results: (jobPositionId) =>
        authGet(`/job-position/${jobPositionId}/analytics/interview-eval3-results/`),
    getEval3Candidate: (jobPositionId, userId, interviewType = 'pre_screening') =>
        authGet(`/job-position/${jobPositionId}/analytics/interview-eval3-candidate/${userId}/?interview_type=${interviewType}`),
    getEval3Status: (jobPositionId) =>
        authGet(`/job-position/${jobPositionId}/analytics/interview-eval3-status/`),
    // Interview flags CRUD (mirrors the resume flags routes)
    getFlags: (jobPositionId) =>
        authGet(`/job-position/${jobPositionId}/interview-screening/flags/`),
    createFlag: (jobPositionId, data = {}) =>
        authPost(`/job-position/${jobPositionId}/interview-screening/flags/create/`, data),
    updateFlag: (jobPositionId, flagId, data = {}) =>
        authPost(`/job-position/${jobPositionId}/interview-screening/flags/${flagId}/update/`, data),
    deleteFlag: (jobPositionId, flagId) =>
        authPost(`/job-position/${jobPositionId}/interview-screening/flags/${flagId}/delete/`),
    reorderFlags: (jobPositionId, data = {}) =>
        authPost(`/job-position/${jobPositionId}/interview-screening/flags/reorder/`, data),
};

/**
 * eval4 (Appli 4.0) work-trial routes.
 *
 * Every path here is served by the Django HR backend (`applicant`): the portal never
 * calls the jobs service or the admin backend directly, so the constants endpoint
 * arrives through the HR backend's proxy like everything else. Built on the same
 * authGet/authPost wrappers as the rest of this file — no second axios instance, and
 * no firebase import reaches the eval4 chunk.
 *
 * One position per call. Every reader takes a single `jobPositionId` string; no
 * function accepts a list of positions or any cross-position argument, which is the
 * portal half of the "scores are never compared across job positions" invariant.
 *
 * Readers resolve to the axios response, like `interviewScreeningApi` above. The two
 * exceptions say so on themselves: `getConstants` resolves to the payload (it is
 * served from cache after the first call) and `getTrialOps` resolves to a merged
 * object (it is a composite of three routes).
 */

/**
 * `eval-config` is keyed on an owner type, and this portal addresses exactly one of
 * them. Certificate structure-version configs are authored in the admin portal and are
 * unreachable from here: the owner type is a constant, never an argument. The url
 * segment is the dashed spelling of the same owner type — collections use dashes,
 * fields use underscores. The HR backend re-derives the owner from the url and rejects
 * one supplied in a body, so this hard-coding is a convenience, not the boundary.
 */
export const EVAL4_OWNER_TYPE = 'job_position';
const EVAL4_OWNER_SEGMENT = EVAL4_OWNER_TYPE.replace(/_/g, '-');

/** Cache key for the constant definitions bundle. Definitions ship with a deploy. */
export const EVAL4_CONSTANTS_CACHE_KEY = 'eval4_constants';

const eval4Position = (jobPositionId) =>
    `/${EVAL4_OWNER_SEGMENT}/${encodeURIComponent(jobPositionId)}`;

const eval4Trial = (jobPositionId, userId) =>
    `${eval4Position(jobPositionId)}/applications/${encodeURIComponent(userId)}/work-trial`;

/** Query string from defined, non-empty params only; `{}` yields ''. */
const eval4Query = (params = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            search.append(key, value);
        }
    });
    const query = search.toString();
    return query ? `?${query}` : '';
};

// The HR backend answers 400 to an owner type or id inside a config body, because the
// owner is the position in the url. Drop them rather than send a request that cannot
// succeed if a caller ever hands them over.
const withoutOwnerKeys = (body = {}) => {
    const { owner_type, owner_id, ...rest } = body;
    return rest;
};

// The constants bundle is definitions the engine compiles in, identical for every
// position, so one entry serves the whole session. The memo is what makes a second
// call free when the localStorage cache is unavailable, and it carries the email that
// cache is keyed on so signing in as someone else is a miss rather than a hit. The
// in-flight promise is what makes concurrent callers share one request, and the
// generation is what stops a read that was already in flight when the cache was
// cleared from writing its now-stale answer back.
let eval4ConstantsMemo = null;
let eval4ConstantsRequest = null;
let eval4ConstantsGeneration = 0;

// Returns the memo entry, not the payload: a bundle that deserialized to something
// falsy is still a hit.
const readCachedEval4Constants = () => {
    const email = getEmail();
    if (eval4ConstantsMemo
        && eval4ConstantsMemo.email === email
        && Date.now() - eval4ConstantsMemo.fetchedAt < TTL.LONG) {
        return eval4ConstantsMemo;
    }
    const cached = getCache(EVAL4_CONSTANTS_CACHE_KEY, { allowStale: false });
    if (cached) {
        eval4ConstantsMemo = { payload: cached.data, fetchedAt: cached.timestamp, email };
        return eval4ConstantsMemo;
    }
    return null;
};

export const eval4Api = {
    // ---- definitions -----------------------------------------------------------
    /**
     * Competency / criteria / tag definitions, read-only and cached for the session.
     * Resolves to the payload rather than the response. The position id addresses the
     * proxy route and authorizes the caller; the definitions it returns are the same
     * for every position.
     */
    getConstants: (jobPositionId) => {
        const cached = readCachedEval4Constants();
        if (cached) return Promise.resolve(cached.payload);
        if (!eval4ConstantsRequest) {
            const generation = eval4ConstantsGeneration;
            eval4ConstantsRequest = authGet(`${eval4Position(jobPositionId)}/eval4/constants/`)
                .then((response) => {
                    const payload = response.data;
                    if (generation === eval4ConstantsGeneration) {
                        eval4ConstantsMemo = { payload, fetchedAt: Date.now(), email: getEmail() };
                        setCache(EVAL4_CONSTANTS_CACHE_KEY, payload, { ttl: TTL.LONG });
                        eval4ConstantsRequest = null;
                    }
                    return payload;
                })
                .catch((error) => {
                    if (generation === eval4ConstantsGeneration) eval4ConstantsRequest = null;
                    throw error;
                });
        }
        return eval4ConstantsRequest;
    },
    /**
     * Drop the cached definitions so the next read goes to the network. A read already
     * in flight still resolves for whoever asked, but it no longer writes: the caller
     * that cleared the cache asked for the bundle that comes after this point.
     */
    clearConstantsCache: () => {
        eval4ConstantsGeneration += 1;
        eval4ConstantsMemo = null;
        eval4ConstantsRequest = null;
        invalidateCache(EVAL4_CONSTANTS_CACHE_KEY);
    },

    // ---- reports ---------------------------------------------------------------
    /** Cohort-level read: one row per candidate scored by an eval4 run. */
    getReportList: (jobPositionId, { runId, limit, offset } = {}) =>
        authGet(`${eval4Position(jobPositionId)}/eval4/reports/${eval4Query({ run_id: runId, limit, offset })}`),
    /** One candidate's report document. */
    getReport: (jobPositionId, userId, { runId } = {}) =>
        authGet(`${eval4Position(jobPositionId)}/eval4/reports/${encodeURIComponent(userId)}/${eval4Query({ run_id: runId })}`),
    /** The run's retry / skip / absence-attribution rows for one candidate. */
    getAnalysisLog: (jobPositionId, userId, { runId, limit } = {}) =>
        authGet(`${eval4Position(jobPositionId)}/eval4/reports/${encodeURIComponent(userId)}/analysis-log/${eval4Query({ run_id: runId, limit })}`),

    // ---- config ----------------------------------------------------------------
    /** The position's eval config. The response carries `editable`; the write gate is server-side. */
    getConfig: (jobPositionId) =>
        authGet(`${eval4Position(jobPositionId)}/eval4/config/`),
    saveConfig: (jobPositionId, body = {}) =>
        authPost(`${eval4Position(jobPositionId)}/eval4/config/update/`, withoutOwnerKeys(body)),
    /**
     * One project template's unified structure — steps, parts, tags, coverage
     * weights and the rest of that set. Its own route, not the multipart
     * `project-templates/update/`: that view rewrites `task_files` from what the
     * request carries and assigns `solution` unconditionally, so a caller that
     * sent only a structure would blank the solution and drop every task file.
     * The response reports how many live projects the save was pushed onto.
     */
    saveProjectStructure: (jobPositionId, projectTemplateId, structure) =>
        authPost(`${eval4Position(jobPositionId)}/project-templates/structure/`, {
            project_template_id: projectTemplateId,
            structure
        }),
    getBypassTelemetry: (jobPositionId) =>
        authGet(`${eval4Position(jobPositionId)}/eval4/bypass-telemetry/`),
    recordBypass: (jobPositionId, entry = {}) =>
        authPost(`${eval4Position(jobPositionId)}/eval4/bypass-telemetry/record/`, withoutOwnerKeys(entry)),

    // ---- one trial's operations ------------------------------------------------
    /**
     * One trial's HR operations surface. The backend serves it as three routes rather
     * than one, so this composes them and resolves to `{timeline, questions, changeLog}`
     * payloads; each request still carries exactly one position id.
     */
    getTrialOps: (jobPositionId, userId) =>
        Promise.all([
            eval4Api.getTimeline(jobPositionId, userId),
            eval4Api.getQuestions(jobPositionId, userId),
            eval4Api.getChangeLog(jobPositionId, userId),
        ]).then(([timeline, questions, changeLog]) => ({
            timeline: timeline.data,
            questions: questions.data,
            changeLog: changeLog.data,
        })),
    /** Merged event / request / submission / Q&A spine for one trial, HR-visible. */
    getTimeline: (jobPositionId, userId, { limit, before } = {}) =>
        authGet(`${eval4Trial(jobPositionId, userId)}/timeline/${eval4Query({ limit, before })}`),

    // ---- requests --------------------------------------------------------------
    getRequests: (jobPositionId, { status, type, limit, cursor } = {}) =>
        authGet(`${eval4Position(jobPositionId)}/trial-requests/${eval4Query({ status, type, limit, cursor })}`),
    /** `{decision: 'approved'|'denied', granted_value?, note?}` — the backend owns the double-approve guard. */
    resolveRequest: (jobPositionId, requestId, body = {}) =>
        authPost(`${eval4Position(jobPositionId)}/trial-requests/${encodeURIComponent(requestId)}/decide/`, body),

    // ---- questions -------------------------------------------------------------
    /** One trial's questions. */
    getQuestions: (jobPositionId, userId, { status, limit, before } = {}) =>
        authGet(`${eval4Trial(jobPositionId, userId)}/questions/${eval4Query({ status, limit, before })}`),
    /** The position's queue across its trials. */
    getQuestionQueue: (jobPositionId, { status, limit, cursor } = {}) =>
        authGet(`${eval4Position(jobPositionId)}/trial-questions/${eval4Query({ status, limit, cursor })}`),
    /** `{answer_body?, canned_answer_id?}`; the backend stamps the answering actor. */
    answerQuestion: (jobPositionId, userId, questionId, body = {}) =>
        authPost(`${eval4Trial(jobPositionId, userId)}/questions/${encodeURIComponent(questionId)}/answer/`, body),
    getCannedAnswers: (jobPositionId) =>
        authGet(`${eval4Position(jobPositionId)}/canned-answers/`),

    // ---- change log ------------------------------------------------------------
    getChangeLog: (jobPositionId, userId, { limit, before, entityId } = {}) =>
        authGet(`${eval4Trial(jobPositionId, userId)}/change-log/${eval4Query({ limit, before, entity_id: entityId })}`),
    getPositionChangeLog: (jobPositionId, { limit, before } = {}) =>
        authGet(`${eval4Position(jobPositionId)}/change-log/${eval4Query({ limit, before })}`),

    // ---- chat ------------------------------------------------------------------
    getChannels: (jobPositionId, userId) =>
        authGet(`${eval4Trial(jobPositionId, userId)}/channels/`),
    getChannelMessages: (jobPositionId, userId, channelId, { limit, before, groupId } = {}) =>
        authGet(`${eval4Trial(jobPositionId, userId)}/channels/${encodeURIComponent(channelId)}/messages/${eval4Query({ limit, before, group_id: groupId })}`),
    /**
     * `{body, attachments?}`. The sender identity is the configured workspace HR display
     * name, resolved server-side — a sender in the body is refused, and the real actor is
     * recorded on the operations side.
     */
    sendHrDm: (jobPositionId, userId, body = {}) =>
        authPost(`${eval4Trial(jobPositionId, userId)}/hr-dm/send/`, body),

    // ---- announcements ---------------------------------------------------------
    /** `{event_type, title, body, attachments?, affects?, supersedes?}` — publication is the backend's. */
    postAnnouncement: (jobPositionId, userId, body = {}) =>
        authPost(`${eval4Trial(jobPositionId, userId)}/announcements/`, body),
    getEventReceipt: (jobPositionId, userId, eventId) =>
        authGet(`${eval4Trial(jobPositionId, userId)}/events/${encodeURIComponent(eventId)}/receipt/`),
    /** One announcement's receipts across this position's trials, one row per trial. */
    getAnnouncementReceipts: (jobPositionId, eventItemId) =>
        authGet(`${eval4Position(jobPositionId)}/announcements/${encodeURIComponent(eventItemId)}/receipts/`),

    // ---- submissions -----------------------------------------------------------
    /**
     * A project's submission history. Versions are per step, but the read is per project:
     * the route is unpaged and stamps a `step_id` on every row, and returns the project's
     * per-step latest pointers beside them, so one read serves every step panel and each
     * panel scopes itself. There is no step parameter to send - a step id in the query
     * would be silently ignored, which would read as server-side scoping that is not
     * happening.
     */
    getSubmissionVersions: (jobPositionId, userId, projectId) =>
        authGet(`${eval4Trial(jobPositionId, userId)}/project/${encodeURIComponent(projectId)}/submission-versions/`),
    /** Signed links minted from one version's manifest. */
    getSubmissionVersionFiles: (jobPositionId, userId, projectId, versionId) =>
        authGet(`${eval4Trial(jobPositionId, userId)}/project/${encodeURIComponent(projectId)}/submission-versions/${encodeURIComponent(versionId)}/files/`),
};

export default baseApi;
